import { z } from "zod";

import { brandingColoresSchema, estiloSidebarSchema } from "./branding";

/**
 * Contrato del alta de instituciones (tenants) por UI (ADR 045).
 *
 * El proceso de titulación es el mismo para todos los tenants; lo único que
 * varía es el branding. Por eso el alta captura identidad (slug, nombres,
 * ciudad) + branding (colores). El logo se sube aparte (multipart) tras crear.
 */

/** Slug de subdominio: label DNS en minúsculas. Inmutable post-creación. */
export const institucionSlugSchema = z
  .string()
  .trim()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9-]+$/, 'El slug solo admite minúsculas, dígitos y guiones (ej. "unheval")');

/**
 * Datos del PRIMER ADMIN de la institución (ADR 053 §2). Se capturan en el mismo
 * alta: al crear el tenant se crea su ADMIN "dueño" por invitación (cuenta
 * INACTIVA sin contraseña + token de un solo uso). El SUPER_ADMIN NUNCA fija la
 * contraseña; el invitado la define al activar. Por eso aquí no viaja `password`.
 */
export const adminInicialSchema = z.object({
  nombres: z.string().trim().min(2).max(120),
  apellidos: z.string().trim().min(2).max(120),
  correo: z.string().trim().toLowerCase().email("Correo inválido"),
  documentoIdentidad: z.string().trim().min(8).max(15),
});
export type AdminInicialInput = z.infer<typeof adminInicialSchema>;

export const crearInstitucionSchema = z.object({
  slug: institucionSlugSchema,
  nombre: z.string().trim().min(3).max(200),
  nombreCorto: z.string().trim().min(2).max(40),
  ciudad: z.string().trim().min(2).max(80),
  /** Paleta de marca; reusa el contrato de branding (formato "R G B"). */
  colores: brandingColoresSchema,
  /** Primer ADMIN del tenant; se provisiona por invitación (ADR 053). */
  admin: adminInicialSchema,
});
export type CrearInstitucionInput = z.infer<typeof crearInstitucionSchema>;

/** Estado de la institución (espejo del enum Prisma EstadoInstitucion). */
export const estadoInstitucionSchema = z.enum(["ACTIVA", "SUSPENDIDA"]);
export type EstadoInstitucion = z.infer<typeof estadoInstitucionSchema>;

/**
 * Subconjunto de branding que el ADMIN de una institución edita en su propio
 * panel de personalización (ADR 047). Todo opcional (semántica PATCH): el
 * service mergea sobre el branding actual. NO incluye la identidad legal
 * (`slug`, `nombre`, `nombreCorto`) — eso lo gestiona el SUPER_ADMIN; aquí se
 * personaliza la presentación (colores, estilo de barra, nombre del sistema).
 *
 * El `institucionId` NUNCA viaja en el body: lo deriva el backend del usuario
 * autenticado, de modo que un ADMIN sólo puede tocar SU tenant.
 */
export const actualizarBrandingSchema = z
  .object({
    nombreSistema: z.string().trim().min(1).max(120),
    nombreSistemaCorto: z.string().trim().min(1).max(60),
    colores: brandingColoresSchema,
    sidebar: estiloSidebarSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, {
    message: "Envía al menos un campo a actualizar",
  });
export type ActualizarBrandingInput = z.infer<typeof actualizarBrandingSchema>;

/** Forma pública de una institución en listados de plataforma. */
export interface InstitucionWire {
  id: string;
  slug: string;
  nombre: string;
  nombreCorto: string;
  ciudad: string;
  /** true si la institución tiene logo subido. */
  tieneLogo: boolean;
  estado: EstadoInstitucion;
  fechaCreacion: string;
}
